import { Component, computed, effect, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, switchMap, throwError } from 'rxjs';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AutocompleteConfig, DynamicFilterConfig, SiteService, createDynamicFilterState } from '@card-software/library';
import { FinancialAccount } from '../../../models/financial-account/financial-account';
import {
  JournalEntry,
  JournalEntrySearchCommand,
  JournalEntryStatus,
  JournalEntrySummary,
  PostingModule,
} from '../../../models/journal-entry/journal-entry';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { JournalEntryProxyService } from '../../../proxies/journal-entry.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debouncedServerSearch } from '@card-software/nexus-utilities';

/** The shape the dynamic-filters panel emits — raw control values, before narrowing to the search command.
 * A value restored from the URL arrives as a string, which is why the numeric and date fields admit one. */
interface JournalEntryFilterValues {
  searchText?: string | null;
  status?: number | string | null;
  sourceModule?: number | string | null;
  period?: string | null;
  account?: FinancialAccount | string | null;
  postingDateFrom?: Date | string | null;
  postingDateTo?: Date | string | null;
}

/** How far back the Period filter offers, in months. Two years covers the current and prior fiscal year,
 * which is as far as anyone reconciles from a dashboard; older work goes through the date range. */
const PERIOD_OPTION_MONTHS = 24;

/**
 * Journal Entries dashboard (Task #5025) — the ledger's front door. Every posting in the system lands here,
 * whichever module produced it, so the filters lead with Source and Period rather than with anything
 * AP-specific.
 *
 * Read-only by design: entries are written by the posting services (`GlPostingHelper`), never from this
 * screen, so there is no create action and no row-level editing.
 */
@Component({
  selector: 'app-journal-entries',
  templateUrl: './journal-entries.component.html',
  styleUrl: './journal-entries.component.scss',
})
export class JournalEntriesComponent {
  private readonly router = inject(Router);
  private readonly service = inject(JournalEntryProxyService);
  private readonly accountService = inject(FinancialAccountProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly locale = inject(LocaleHelperService);

  private lastSearchCriteria: JournalEntrySearchCommand = {};

  private statusLabels: Record<number, string> = {};
  private sourceModuleLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  isLoading = signal(0);
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'journal-entries-filters', config: this.filterConfig });
  entries = signal<JournalEntry[]>([]);
  summary = signal<JournalEntrySummary | null>(null);

  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  /**
   * Sort state, applied server-side (#5028).
   *
   * <p>Defaults to the entry number, highest first. The number is assigned in posting order and zero-padded
   * (FI-2026-000031), so descending IS newest-first — and unlike the posting date it is unique, which is
   * what makes paging safe: a day's entries all share one date, so a date sort can hand the same entry to
   * two pages and never show another at all.</p>
   */
  readonly sortField = signal<string>('number');
  readonly sortOrder = signal<number>(-1);

  /** Currency the tiles are expressed in — the company's books currency, not any one entry's. */
  readonly summaryCurrency = computed(() => this.currencyLabel(this.summary()?.functionalCurrency));

  /** Mutated in place — the dynamic-filters panel holds this object and reads `suggestions` off it. */
  private readonly accountQueries$ = new Subject<string>();

  readonly accountAutocompleteConfig: AutocompleteConfig = {
    suggestions: [],
    optionLabel: 'displayLabel',
    optionValue: 'id',
    completeMethod: (event: AutoCompleteCompleteEvent) => this.accountQueries$.next(event.query),
    resolveById: (id: string) => this.resolveAccount(id),
    dropdown: true,
    forceSelection: true,
  };

  constructor() {
    // No element to hang ServerSearchDirective on — the dynamic-filters panel renders the
    // autocomplete — so the operator is wired by hand here.
    this.accountQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchAccounts(query)),
        takeUntilDestroyed(),
      )
      .subscribe((suggestions) => (this.accountAutocompleteConfig.suggestions = suggestions));

    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as JournalEntryFilterValues));
    this.loadSummary();

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.statusLabels = this.enumService.getEnum('JournalEntryStatus').values as Record<number, string>;
        this.sourceModuleLabels = this.enumService.getEnum('PostingModule', 'finance').values as Record<number, string>;
        this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  // ---- filters ----

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'searchText',
          label: this.t('JournalEntries_Search', 'Search'),
          placeholder: this.t('JournalEntries_Search_Placeholder', 'Entry #, reference or description'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'sourceModule',
          label: this.t('JournalEntry_Source', 'Source'),
          placeholder: this.t('JournalEntries_AllSources', 'All sources'),
          options: this.sourceModuleLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'period',
          label: this.t('JournalEntry_Period', 'Period'),
          placeholder: this.t('JournalEntries_AllPeriods', 'All periods'),
          options: this.buildPeriodOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'account',
          label: this.t('JournalEntry_Account', 'Account'),
          placeholder: this.t('JournalEntries_Account_Search', 'Search account'),
          autocompleteConfig: this.accountAutocompleteConfig,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'postingDateFrom',
          label: this.t('JournalEntry_PostingDateFrom', 'Posted from'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'postingDateTo',
          label: this.t('JournalEntry_PostingDateTo', 'Posted to'),
          defaultVisible: false,
        },
      ],
    };
  }

  /**
   * `YYYY-MM` options, newest first. GL periods are calendar months — `GlPostingHelper` takes both the year
   * and the period straight off the posting date — so these can be generated rather than fetched.
   */
  private buildPeriodOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [];
    const cursor = new Date();
    cursor.setDate(1);
    for (let i = 0; i < PERIOD_OPTION_MONTHS; i++) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      options.push({ label: key, value: key });
      cursor.setMonth(cursor.getMonth() - 1);
    }
    return options;
  }

  private searchAccounts(query: string): Observable<FinancialAccount[]> {
    return this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).pipe(
      switchMap((companyId) =>
        companyId
          ? // matchAny: the query is one box, so it has to hit either the number or the name.
            this.accountService.search(
              { accountNumber: query, accountName: query, financialCompanyReference: companyId, isPostingAccount: true },
              1,
              10,
              true,
            )
          : of(null),
      ),
      map((response) => (response?.data?.items ?? []).map((a) => this.withDisplayLabel(a))),
    );
  }

  private resolveAccount(id: string): Observable<FinancialAccount | null> {
    return this.accountService.getById(id).pipe(
      map((response) => (response.data ? this.withDisplayLabel(response.data) : null)),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  /** The autocomplete renders one string per option, so number and name are joined up-front. */
  private withDisplayLabel(account: FinancialAccount): FinancialAccount & { displayLabel: string } {
    return { ...account, displayLabel: `${account.accountNumber}  ${this.locale.getLocalizedValue(account.accountName)}`.trim() };
  }

  private searchWithFilters(filters: JournalEntryFilterValues): void {
    const [fiscalYear, fiscalPeriod] = this.splitPeriod(filters.period);
    const account = filters.account;

    const criteria: JournalEntrySearchCommand = {
      searchText: filters.searchText || null,
      status: this.toNumber(filters.status),
      sourceModule: this.toNumber(filters.sourceModule),
      fiscalYear,
      fiscalPeriod,
      accountReference: typeof account === 'string' ? account || null : (account?.id ?? null),
      postingDateFrom: this.toIso(filters.postingDateFrom),
      postingDateTo: this.toIso(filters.postingDateTo),
    };

    this.currentPage.set(1);
    this.search(criteria);
  }

  /** `2026-06` → `[2026, 6]`. A malformed or absent value filters on neither, rather than on year 0. */
  private splitPeriod(period: string | null | undefined): [number | null, number | null] {
    if (!period) return [null, null];
    const [year, month] = period.split('-').map(Number);
    return Number.isFinite(year) && Number.isFinite(month) ? [year, month] : [null, null];
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toIso(value: unknown): string | null {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : String(value);
  }

  // ---- data ----

  private loadSummary(): void {
    this.startLoading();
    this.service
      .getSummary()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.summary.set(response?.data ?? null),
        error: () => this.summary.set(null),
      });
  }

  private search(criteria: JournalEntrySearchCommand): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.entries.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.entries.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  /**
   * Re-queries on a column sort. Back to page one, because a sorted page 3 is three pages into an order
   * that no longer exists. Guarded, so a sort that changed nothing does not re-fetch.
   */
  onSort(event: SortEvent): void {
    const field = event.field ?? 'number';
    const order = event.order ?? -1;
    if (field === this.sortField() && order === this.sortOrder()) return;

    this.sortField.set(field);
    this.sortOrder.set(order);
    this.currentPage.set(1);
    this.search(this.lastSearchCriteria);
  }

  /** #5077 — opens a blank manual entry. Drafts are reopened through the row click like any other entry;
   *  the manual screen sends anything that is not an editable draft on to the read-only details page. */
  createManualEntry(): void {
    this.router.navigate(['/journal-entry-manual']);
  }

  /**
   * A Draft this screen produced is still editable, so it opens in the manual form; everything else is
   * either posted (immutable) or owned by the module that wrote it, and opens read-only.
   */
  redirectToDetails(id?: string | null): void {
    if (!id) return;
    const entry = this.entries().find((candidate) => candidate.id === id);
    const isEditableDraft = entry?.status === JournalEntryStatus.Draft && entry?.sourceModule === PostingModule.GeneralLedger;
    this.router.navigate([isEditableDraft ? '/journal-entry-manual' : '/journal-entry-details'], { queryParams: { id } });
  }

  // ---- display ----

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  sourceModuleLabel(module: number): string {
    return this.sourceModuleLabels[module] ?? '';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case JournalEntryStatus.Posted:
        return 'success';
      case JournalEntryStatus.Draft:
        return 'warn';
      case JournalEntryStatus.Reversed:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /** `2026-06` for the Period column — the same shape the Period filter offers. */
  periodLabel(entry: JournalEntry): string {
    if (!entry.fiscalYear || !entry.fiscalPeriod) return '';
    return `${entry.fiscalYear}-${String(entry.fiscalPeriod).padStart(2, '0')}`;
  }

  getRowData(rowData: unknown): JournalEntry {
    return rowData as JournalEntry;
  }
}
